/**
 * Filterstatus van de B2B Discovery Engine: leeft in de URL (?q=…&vorm=…&fiscaal=…) zodat een
 * zoekopdracht van de HR-manager deelbaar en herlaadbaar is.
 */
import type { B2bFiscaalFilter, B2bGroepsgrootte, B2bInkoopType, B2bLeverlocatie, B2bRij, B2bSortering } from '../../services/b2bAcademyApi';

export interface B2bFilters {
  q: string | null;
  rij: B2bRij | null;
  vorm: B2bInkoopType | null;
  fiscaal: B2bFiscaalFilter[];
  locatie: B2bLeverlocatie[];
  groep: B2bGroepsgrootte | null;
  sort: B2bSortering;
  /** "Alle incompany-trajecten van A tot Z" */
  alles: boolean;
}

export const LEGE_B2B_FILTERS: B2bFilters = { q: null, rij: null, vorm: null, fiscaal: [], locatie: [], groep: null, sort: 'relevantie', alles: false };

const RIJEN: B2bRij[] = ['mentaal', 'fysiek', 'vakinhoudelijk', 'passie'];
const FISCAAL: B2bFiscaalFilter[] = ['vrijgesteld', 'wkr'];
const LOCATIES: B2bLeverlocatie[] = ['eigen_locatie', 'extern', 'online'];
const INKOOP: B2bInkoopType[] = ['incompany', 'bulk_seats', 'licentie'];
const SORTERINGEN: B2bSortering[] = ['relevantie', 'populair', 'roi', 'impact', 'prijs'];
/** Bovengrens van de vrije groepsgrootte (gelijk aan de backend). */
export const MAX_GROEP = 10_000;

const lijst = <T extends string>(raw: string | null, toegestaan: T[]): T[] =>
  [...new Set((raw ?? '').split(',').map((v) => v.trim()).filter((v): v is T => toegestaan.includes(v as T)))];

/** "12" → 12; leeg, 0, decimaal of buiten 1 – 10.000 → null. */
export function groepUit(raw: string | null): B2bGroepsgrootte | null {
  if (!raw || !/^\d{1,5}$/.test(raw.trim())) return null;
  const n = parseInt(raw, 10);
  return n >= 1 && n <= MAX_GROEP ? n : null;
}

export function b2bFiltersUitParams(sp: URLSearchParams): B2bFilters {
  const rij = sp.get('rij');
  const vorm = sp.get('vorm');
  const groep = sp.get('groep');
  const sort = sp.get('sort');
  return {
    q: sp.get('q')?.trim() || null,
    rij: RIJEN.includes(rij as B2bRij) ? (rij as B2bRij) : null,
    vorm: INKOOP.includes(vorm as B2bInkoopType) ? (vorm as B2bInkoopType) : null,
    fiscaal: lijst(sp.get('fiscaal'), FISCAAL),
    locatie: lijst(sp.get('locatie'), LOCATIES),
    groep: groepUit(groep),
    sort: SORTERINGEN.includes(sort as B2bSortering) ? (sort as B2bSortering) : 'relevantie',
    alles: sp.get('alles') === '1',
  };
}

/** Schrijft de filters terug in de URL; overige params (bijv. ?rfq=) blijven staan. */
export function paramsMetB2bFilters(bestaand: URLSearchParams, f: B2bFilters): URLSearchParams {
  const sp = new URLSearchParams(bestaand);
  for (const key of ['q', 'rij', 'vorm', 'fiscaal', 'locatie', 'groep', 'sort', 'alles']) sp.delete(key);
  if (f.q) sp.set('q', f.q);
  if (f.rij) sp.set('rij', f.rij);
  if (f.vorm) sp.set('vorm', f.vorm);
  if (f.fiscaal.length) sp.set('fiscaal', f.fiscaal.join(','));
  if (f.locatie.length) sp.set('locatie', f.locatie.join(','));
  if (f.groep) sp.set('groep', String(f.groep));
  if (f.sort !== 'relevantie') sp.set('sort', f.sort);
  if (f.alles) sp.set('alles', '1');
  return sp;
}

/** Zodra er getypt of gefilterd wordt, vervagen de Netflix-rijen en klapt het grid uit. */
export function heeftB2bFilters(f: B2bFilters): boolean {
  return Boolean(f.q || f.rij || f.vorm || f.fiscaal.length || f.locatie.length || f.groep || f.alles);
}
